import React, { useState, useEffect } from "react";
import { keyframes } from "@emotion/react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
} from "@mui/material";
import {
  Add,
  Edit,
  Assignment,
  Delete,
  Download,
  Landscape,
  Description,
  UploadFile
} from "@mui/icons-material";
import { ref, getBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../components/firebase.config'; // adjust path to your firebase config

import { collection, onSnapshot, deleteDoc, doc, addDoc, query, where } from "firebase/firestore";
import { firestore as db } from "../components/firebase.config";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Document, Packer, Paragraph, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType, AlignmentType, HeadingLevel, PageOrientation, ImageRun
} from "docx";
import { useAuth } from '../auth/AuthContext';


const AdminChecklistView = ({ onEditChecklist }) => {
  const [checklists, setChecklists] = useState([]);
  const [assigningChecklist, setAssigningChecklist] = useState(null);
  const [assignEmail, setAssignEmail] = useState("");
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);

  // Alert state for export feedback
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showAlert, setShowAlert] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

 const { currentUser, isSuperAdmin, isAppAdmin, tenantId } = useAuth();
 const userEmail = currentUser?.email || '';

  useEffect(() => {
    console.log('AdminChecklistView: Starting data fetch');
    console.log('isSuperAdmin:', isSuperAdmin);
    console.log('tenantId:', tenantId);

    // Auth check — AuthContext is the source of truth.
    if (!userEmail) {
      console.error('User not authenticated');
      setChecklists([]);
      return;
    }
    // Per matrix: SuperAdmin has NO checklist access. Only AppAdmin/AppUser do.
    // AppAdmin must have a tenantId.
    if (!isAppAdmin && !isSuperAdmin) {
      setChecklists([]);
      return;
    }

    let q;
    if (isSuperAdmin) {
      q = collection(db, "auditchecklists");
      console.log('Fetching all checklists (super admin)');
    } else {
      q = query(collection(db, "auditchecklists"), where("tenantId", "==", tenantId));
      console.log('Fetching checklists for tenantId:', tenantId);
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('Snapshot received, docs count:', snapshot.docs.length);
      const fetchedChecklists = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log('Fetched checklists:', fetchedChecklists);
      setChecklists(fetchedChecklists);
    }, (error) => {
      console.error('Error fetching checklists:', error);
      if (error.code === 'permission-denied') {
        console.error('Permission denied - check Firebase security rules');
        setAlertMessage('Permission denied. Please check your access rights or contact administrator.');
        setAlertSeverity('error');
        setShowAlert(true);
      }
    });

    return () => unsubscribe();
   }, [tenantId, isSuperAdmin]);

  const handleDeleteChecklist = async (id) => {
    try {

      // Fixed: Use correct collection name
      await deleteDoc(doc(db, "auditchecklists", id));
    } catch (error) {
      console.error("Error deleting checklist:", error);
    }
  };

  // Proof-of-concept: Fetch one image from Firebase Storage and put it in a Word doc


const generatePDF = async (checklist) => {
  try {
    setIsGeneratingPDF(true);
    const pdfDoc = new jsPDF({orientation:'landscape'});

    // Set headers based on the columns
    const headers = checklist.columns?.map((col) => col.name) || [];

    // Helper function to get image bytes from Firebase Storage
    // Fetch bytes from Firebase or direct URL
const getImageBytesFromFirebase = async (url) => {
  try {
    console.log('Fetching image from Firebase URL:', url);

    // Try Firebase SDK first
    try {
      const urlObj = new URL(url);
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
      if (pathMatch) {
        const encodedPath = pathMatch[1].split("?")[0];
        const storagePath = decodeURIComponent(encodedPath);
        console.log('Decoded storage path:', storagePath);

        const fileRef = ref(storage, storagePath);
        console.log('File reference created, getting bytes...');
        const bytes = await getBytes(fileRef);
        console.log('Bytes received, size:', bytes.byteLength);
        return bytes;
      }
    } catch (firebaseError) {
      console.log('Firebase SDK failed, trying direct fetch:', firebaseError);
    }

    // Fallback to direct URL fetch
    console.log('Trying direct URL fetch...');
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    console.log('Direct fetch successful, bytes received:', bytes.length);
    return bytes;

  } catch (err) {
    console.error("Error getting image bytes:", err);
    return null;
  }
};

// Helper: check if file is an image
const isImageFile = (url) => {
  const extension = url.split('.').pop()?.toLowerCase();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

  const urlLower = url.toLowerCase();
  const hasImageKeywords =
    urlLower.includes('image') || urlLower.includes('photo') || urlLower.includes('img');

  return imageExtensions.includes(extension) || hasImageKeywords;
};

// Helper: convert Uint8Array (bytes) → Base64
const bytesToBase64 = (bytes) => {
  let binary = '';
  const chunkSize = 0x8000; // 32k
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunkSize)
    );
  }
  return btoa(binary);
};

// Add image to PDF safely
const addImageToPDF = (pdfDoc, imageBytes, x, y, width = 80, height = 60) => {
  try {
    console.log('Adding image to PDF at position:', x, y);

    const base64 = bytesToBase64(imageBytes);
    const base64Image = `data:image/jpeg;base64,${base64}`;

    pdfDoc.addImage(base64Image, 'JPEG', x, y, width, height);

    console.log('Image added successfully to PDF');
    return height + 3; // return vertical spacing for next element
  } catch (error) {
    console.error("Error adding image to PDF:", error);
    return 0;
  }
};


    // Handle both sections and rows format
    let rows = [];
    let rowHeights = [];

    if (checklist.sections) {
      // New sections format
      checklist.sections.forEach(section => {
        // Add section header row
        rows.push([section.type, section.title, '', '', '', '', '', '', '']);
        rowHeights.push(15); // Standard height for section headers

        // Add section data rows
        section.rows?.forEach(row => {
          const rowData = checklist.columns?.map((col) => {
            const cellData = row.data?.[col.id];
            if (col.name === "Attachments" && cellData && typeof cellData === 'object') {
              // Only include values that look like URLs
              return Object.values(cellData)
                .filter(url => typeof url === 'string' && url.startsWith('http'))
                .join(', ');
            }
            if (typeof cellData === 'object' && cellData !== null) {
              // For attachment objects, show available URLs
              const urls = Object.entries(cellData)
                .filter(([key, value]) => key.includes('Url') && value)
                .map(([key, value]) => key.replace('Url', ''))
                .join(', ');
              return urls || '';
            }
            return cellData || "";
          }) || [];
          rows.push(rowData);
          rowHeights.push(20); // Standard height for data rows
        });
      });
    } else if (checklist.rows) {
      // Old rows format (fallback)
      rows = checklist.rows?.map((row) => {
        const rowData = checklist.columns?.map((col) => {
          const cellData = row.data?.[col.id];
          if (typeof cellData === 'object' && cellData !== null) {
            const urls = Object.entries(cellData)
              .filter(([key, value]) => key.includes('Url') && value)
              .map(([key, value]) => key.replace('Url', ''))
              .join(', ');
            return urls || '';
          }
          return cellData || "";
        }) || [];
        rowHeights.push(20);
        return rowData;
      }) || [];
    }

    // Add the checklist name as the title
    pdfDoc.setFontSize(16);
    pdfDoc.text(checklist.name || "Untitled Checklist", 10, 15);

    // Add description if available
    let startY = 25;
    if (checklist.description) {
      pdfDoc.setFontSize(10);
      pdfDoc.text(`Description: ${checklist.description}`, 10, startY);
      startY += 10;
    }

    // Create the table using autoTable function
    autoTable(pdfDoc, {
      head: headers.length ? [headers] : [],
      body: rows,
      startY: startY,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        // Set specific width for attachments column to accommodate images
        [headers.findIndex(h => h === 'Attachments')]: { cellWidth: 80, minCellHeight: 60 }
      },
      margin: { top: 10 },
      rowStyles: {
        minCellHeight: 60, // Increased height to accommodate images
      }
    });

    // Get the final Y position from the last table
    let yOffset = pdfDoc.lastAutoTable ? pdfDoc.lastAutoTable.finalY + 15 : startY + 20;

    // Add remarks section at the bottom of the table
    if (checklist.remarks) {
      pdfDoc.setFontSize(10);
      pdfDoc.text(`Remarks: ${checklist.remarks}`, 10, yOffset);
      yOffset += 10;
    }

    // Add main image if present
    if (checklist.imageUrl) {
      try {
        const imageBytes = await getImageBytesFromFirebase(checklist.imageUrl);
        if (imageBytes) {
          const imageHeight = await addImageToPDF(pdfDoc, imageBytes, 10, yOffset, 150, 112);
          yOffset += imageHeight;
        } else {
          pdfDoc.setFontSize(10);
          pdfDoc.text(`Main Image: ${checklist.imageUrl}`, 10, yOffset);
          yOffset += 10;
        }
      } catch (error) {
        console.error("Error adding image to PDF:", error);
        pdfDoc.setFontSize(10);
        pdfDoc.text(`Main Image: ${checklist.imageUrl}`, 10, yOffset);
        yOffset += 10;
      }
    }

    // Process attachments inline within the table structure
    let totalAttachments = 0;
    let processedImages = 0;
    let failedImages = 0;

    // Get the table position and dimensions
    const tableData = pdfDoc.lastAutoTable;
    if (tableData && checklist.sections) {
      console.log('Table data:', tableData);

      // Use a simpler approach - add images after the table with proper positioning
      let currentY = tableData.finalY + 10;

      for (const section of checklist.sections) {
        for (const row of section.rows || []) {
          // Find attachments column
          const attachmentColIndex = checklist.columns?.findIndex(col => col.name === 'Attachments');
          if (attachmentColIndex !== -1) {
            const cellData = row.data?.[checklist.columns[attachmentColIndex].id];
            if (cellData && typeof cellData === 'object') {
              const urls = Object.values(cellData).filter(url =>
                typeof url === 'string' && url.startsWith('http')
              );

              if (urls.length > 0) {
                totalAttachments += urls.length;

                // Add section and row info
                pdfDoc.setFontSize(8);
                pdfDoc.text(`Section: ${section.type} - ${section.title}`, 10, currentY);
                currentY += 5;

                // Add images below the table but organized by row
                let imageX = 10;
                for (const url of urls) {
                  console.log('Processing URL:', url);

                  if (isImageFile(url)) {
                    try {
                      console.log('Processing image URL:', url);
                      const imageBytes = await getImageBytesFromFirebase(url);
                      if (imageBytes) {
                        console.log('Image bytes received, adding to PDF');
                        // Add image with proper spacing
                        addImageToPDF(pdfDoc, imageBytes, imageX, currentY, 80, 60);

                        // Add caption below image
                        pdfDoc.setFontSize(6);
                        const fileName = url.split('/').pop();
                        pdfDoc.text(fileName, imageX, currentY + 65);

                        processedImages++;
                        imageX += 90; // Space for next image

                        // Check if we need to wrap to next line
                        if (imageX > pdfDoc.internal.pageSize.width - 100) {
                          imageX = 10;
                          currentY += 80;
                        }
                      } else {
                        console.log('No image bytes received for URL:', url);
                        failedImages++;
                      }
                    } catch (error) {
                      console.error("Error processing attachment:", error);
                      failedImages++;
                    }
                  }
                }

                currentY += 80; // Space for next row

                // Check if we need a new page
                if (currentY > pdfDoc.internal.pageSize.height - 100) {
                  pdfDoc.addPage();
                  currentY = 20;
                }
              }
            }
          }
        }
      }
    }

    // Add attachment summary at the end
    if (totalAttachments > 0) {
      const finalY = pdfDoc.lastAutoTable ? pdfDoc.lastAutoTable.finalY : yOffset;
      pdfDoc.setFontSize(10);
      pdfDoc.text(`Images processed: ${processedImages}/${totalAttachments}`, 10, finalY + 20);

      // Add a test rectangle to verify positioning
      pdfDoc.setDrawColor(255, 0, 0);
      pdfDoc.rect(10, finalY + 30, 50, 30);
      pdfDoc.text('Test Rectangle', 10, finalY + 70);

      // Add test image processing info
      pdfDoc.text(`Total URLs found: ${totalAttachments}`, 10, finalY + 90);
      pdfDoc.text(`Failed images: ${failedImages}`, 10, finalY + 100);
    } else {
      // Add a test rectangle even when no attachments to verify basic functionality
      const finalY = pdfDoc.lastAutoTable ? pdfDoc.lastAutoTable.finalY : yOffset;
      pdfDoc.setDrawColor(0, 255, 0);
      pdfDoc.rect(10, finalY + 20, 50, 30);
      pdfDoc.text('No Attachments - Test Rectangle', 10, finalY + 60);
    }

    // Save the PDF with the checklist name
    const fileName = checklist.name ? `${checklist.name.replace(/[^a-z0-9]/gi, '_')}.pdf` : "Checklist.pdf";
    pdfDoc.save(fileName);

    setAlertMessage('PDF generated successfully with inline images!');
    setAlertSeverity('success');
    setShowAlert(true);

  } catch (error) {
    console.error("Error generating PDF:", error);
    setAlertMessage('Error generating PDF. Please try again.');
    setAlertSeverity('error');
    setShowAlert(true);
  } finally {
    setIsGeneratingPDF(false);
  }
};



// Change function signature to accept checklist
const handleExportWord = async (checklist) => {
  try {
    // const fetchBytesAndType = async (url) => {
    // 	 const res = await fetch(url, { cache: 'no-store' });
    // 	 if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
    // 	 const contentType = res.headers.get('Content-Type') || '';
    // 	 const buf = await res.arrayBuffer();
    // 	 return { bytes: new Uint8Array(buf), contentType };
    // };
   const getBytesFromFirebase = async (url) => {
    try {
      // Convert string URL into an object for easier parsing
      const urlObj = new URL(url);

      // Match the encoded storage path from /o/<encoded_path>
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
      if (!pathMatch) {
        throw new Error("Invalid Firebase Storage URL format");
      }

      // Remove query params (after '?') and decode
      const encodedPath = pathMatch[1].split("?")[0];
      const storagePath = decodeURIComponent(encodedPath);

      // Get a reference to the file in Firebase Storage
      const fileRef = ref(storage, storagePath);

      // Download file bytes
      const bytes = await getBytes(fileRef);

      // Guess content type from file extension
      let contentType = "application/octet-stream";
      const ext = storagePath.split('.').pop().toLowerCase();
      const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
      if (imageExts.includes(ext)) {
        contentType = `image/${ext === "jpg" ? "jpeg" : ext}`;
      }

      // Return bytes + type
      return { bytes, contentType };

    } catch (err) {
      console.error("Error in getBytesFromFirebase:", err);
      throw err; // Let caller decide what to do
    }
  };

// (moved below handleExportWord)






    const processAttachmentCell = async (cellData) => {
      if (!cellData || typeof cellData !== 'object') return [new Paragraph({ text: '' })];
      const urls = Object.values(cellData).filter(Boolean);
      const children = [];
      for (const url of urls) {
        try {
          let bytes, contentType;

          // Try Firebase SDK first (more efficient for Firebase Storage files)
          try {
            const result = await getBytesFromFirebase(url);
            bytes = result.bytes;
            contentType = result.contentType;
          } catch (firebaseError) {
            console.log("Firebase SDK failed, falling back to direct URL fetch:", firebaseError);
            // Fallback to direct URL fetch for non-Firebase URLs or if Firebase fails
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const arrayBuffer = await response.arrayBuffer();
            bytes = new Uint8Array(arrayBuffer);
            contentType = response.headers.get('Content-Type') || 'application/octet-stream';
          }

          if (contentType.toLowerCase().startsWith('image/')) {
            children.push(
              new Paragraph({ children: [new ImageRun({ data: bytes, transformation: { width: 200, height: 150 } })] })
            );
          } else {
            children.push(new Paragraph({ text: url }));
          }
        } catch (error) {
          console.error("Failed to process attachment:", error);
          children.push(new Paragraph({ text: url }));
        }
      }
      return children.length ? children : [new Paragraph({ text: '' })];
    };

    const tableRows = [];
    // Header row
    tableRows.push(
      new DocxTableRow({
        children: checklist.columns.map((col) =>
          new DocxTableCell({
            children: [new Paragraph({ text: col.name, alignment: AlignmentType.CENTER })],
            width: {
              size:
                col.name === 'Attachments' ? 15 :
                col.name === 'Guideline prompts' ? 25 :
                col.name === 'Observation' ? 18 :
                col.name === 'MMES Requirements of 3P' ? 22 : 12,
              type: WidthType.PERCENTAGE,
            },
            shading: { fill: 'E0E0E0' },
          })
        ),
        tableHeader: true,
      })
    );

    if (checklist.sections) {
      for (const section of checklist.sections) {
        tableRows.push(
          new DocxTableRow({
            children: [
              new DocxTableCell({
                children: [new Paragraph({ text: `${section.type}. ${section.title}`, alignment: AlignmentType.LEFT })],
                columnSpan: checklist.columns.length,
                shading: { fill: 'FFFF00' },
              }),
            ],
          })
        );

        for (const row of section.rows) {
          const cells = [];
          for (const col of checklist.columns) {
            const cellData = row.data[col.id] || '';
            if (col.name === 'Attachments' && cellData && typeof cellData === 'object') {
              const children = await processAttachmentCell(cellData);
              cells.push(new DocxTableCell({ children, width: { size: 15, type: WidthType.PERCENTAGE }, margins: { top: 100, bottom: 100, left: 100, right: 100 } }));
            } else {
              cells.push(new DocxTableCell({
                children: [new Paragraph({ text: String(cellData), alignment: AlignmentType.LEFT, spacing: { line: 240 } })],
                width: {
                  size:
                    col.name === 'Guideline prompts' ? 25 :
                    col.name === 'Observation' ? 18 :
                    col.name === 'MMES Requirements of 3P' ? 22 : 12,
                  type: WidthType.PERCENTAGE,
                },
                margins: { top: 100, bottom: 100, left: 100, right: 100 },
              }));
            }
          }
          tableRows.push(new DocxTableRow({ children: cells }));
        }
      }
    }

    const children = [
      new Paragraph({ text: 'Audit Checklist Report', heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { after: 400, before: 400 } }),
      ...(checklist.name ? [new Paragraph({ text: checklist.name, heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER, spacing: { after: 300, before: 200 } })] : []),
      ...(checklist.description ? [new Paragraph({ text: `Description: ${checklist.description}`, spacing: { after: 200 } })] : []),
      new DocxTable({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: tableRows }),
      ...(checklist.remarks ? [new Paragraph({ text: checklist.remarks })] : []),
    ];

    if (checklist.imageUrl) {
      try {
        // const { bytes, contentType } = await fetchBytesAndType(checklist.imageUrl);
        const { bytes, contentType } = await getBytesFromFirebase(checklist.imageUrl);

        if (contentType.toLowerCase().startsWith('image/')) {
          children.push(new Paragraph({ children: [new ImageRun({ data: bytes, transformation: { width: 300, height: 225 } })] }));
        } else {
          children.push(new Paragraph({ text: checklist.imageUrl }));
        }
      } catch (_e) {
        children.push(new Paragraph({ text: checklist.imageUrl }));
      }
    }

    const doc = new Document({
      sections: [{ properties: { page: { size: { orientation: PageOrientation.LANDSCAPE } } }, children }],
    });

    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${checklist.name || 'checklist'}_report.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setAlertMessage('Word document generated successfully!');
    setAlertSeverity('success');
    setShowAlert(true);
  } catch (error) {
    console.error('Error generating Word document:', error);
    setAlertMessage('Error generating Word document. Please try again.');
    setAlertSeverity('error');
    setShowAlert(true);
  }
};

// Minimal Word export with selected fields only (component scope)
const handleExportWordMinimal = async (checklist) => {
  try {
    // Show section name as its own row above fields
    const desiredHeaders = ['Sr. No.', 'MMES Requirements', 'Observation', 'Suggestion', 'Attachments'];

    const findColumnByNames = (names) => {
      const lowerNames = names.map((n) => n.toLowerCase());
      return checklist.columns?.find((col) => {
        const n = (col.name || col.id || '').toLowerCase();
        return lowerNames.some((target) => n.includes(target));
      });
    };

    const colSrNo = findColumnByNames(['sr', 'serial']);
    const colRequirement = findColumnByNames(['requirement', 'mmes requirements']);
    const colObservation = findColumnByNames(['observation']);

    const colSuggestion = findColumnByNames(['suggestion']);
    const colRiskZone = findColumnByNames(['risk zone', 'risk zone r/y/g']);

    const colAttachment = findColumnByNames(['attachment']);

    // Helper to fetch bytes from Firebase (if possible) else direct fetch
    const fetchBytesSmart = async (url) => {
      try {
        const urlObj = new URL(url);
        const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
        if (pathMatch) {
          const encodedPath = pathMatch[1].split("?")[0];
          const storagePath = decodeURIComponent(encodedPath);
          const fileRef = ref(storage, storagePath);
          const bytes = await getBytes(fileRef);
          let contentType = 'application/octet-stream';
          const ext = storagePath.split('.').pop().toLowerCase();
          const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
          if (imageExts.includes(ext)) {
            contentType = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
          }
          return { bytes, contentType };
        }
      } catch (_e) {
        // fallthrough to direct fetch
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arr = await res.arrayBuffer();
      const contentType = res.headers.get('Content-Type') || 'application/octet-stream';
      return { bytes: new Uint8Array(arr), contentType };
    };

    const tableRows = [];
    tableRows.push(new DocxTableRow({
      children: desiredHeaders.map((h) => new DocxTableCell({
        children: [new Paragraph({ text: h, alignment: AlignmentType.CENTER })],
        width: { size: 20, type: WidthType.PERCENTAGE },
        shading: { fill: 'E0E0E0' },
      })),
      tableHeader: true,
    }));

    if (checklist.sections) {
      for (const section of checklist.sections) {
        // Section header row spanning all columns
        tableRows.push(
          new DocxTableRow({
            children: [
              new DocxTableCell({
                children: [new Paragraph({ text: `${section.type || ''}${section.type && section.title ? '. ' : ''}${section.title || ''}`.trim() })],
                columnSpan: desiredHeaders.length,
                shading: { fill: 'FFFFCC' },
              }),
            ],
          })
        );
        for (const row of section.rows || []) {
          const getCell = (colRef) => {
            if (!colRef) return '';
            const val = row.data?.[colRef.id];
            if (val === null || val === undefined) return '';
            if (typeof val === 'object') {
              const urls = Object.values(val).filter((v) => typeof v === 'string' && v.startsWith('http'));
              if (urls.length) return urls.join('\n');
              return Object.values(val).filter((v) => typeof v === 'string').join(', ');
            }
            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
            return String(val);
          };

          // Build attachment cell with inline images
          let attachmentChildren = [new Paragraph({ text: '' })];
          const attachmentVal = row.data?.[colAttachment?.id];
          if (attachmentVal && typeof attachmentVal === 'object') {
            const urls = Object.values(attachmentVal).filter(Boolean);
            const children = [];
            for (const url of urls) {
              try {
                const { bytes, contentType } = await fetchBytesSmart(url);
                if (contentType.toLowerCase().startsWith('image/')) {
                  children.push(new Paragraph({ children: [new ImageRun({ data: bytes, transformation: { width: 200, height: 150 } })] }));
                } else {
                  children.push(new Paragraph({ text: url }));
                }
              } catch (_e) {
                children.push(new Paragraph({ text: url }));
              }
            }
            if (children.length) attachmentChildren = children;
          }

          // Get risk zone value and determine background color
          const riskZoneValue = row.data?.[colRiskZone?.id];
          let srNoBackgroundColor = 'FFFFFF'; // Default white

          if (riskZoneValue) {
            switch (riskZoneValue.toLowerCase()) {
              case 'red':
                srNoBackgroundColor = 'FF0000'; // Light red
                break;
              case 'yellow':
                srNoBackgroundColor = 'FFA500'; // Light yellow
                break;
              case 'green':
                srNoBackgroundColor = '008000'; // Light green
                break;
              default:
                srNoBackgroundColor = 'FFFFFF'; // Default white
            }
          }

          const cells = [
            new DocxTableCell({
              children: [new Paragraph({ text: getCell(colSrNo) })],
              shading: { fill: srNoBackgroundColor }
            }),
            new DocxTableCell({ children: [new Paragraph({ text: getCell(colRequirement) })] }),
            new DocxTableCell({ children: [new Paragraph({ text: getCell(colObservation) })] }),

            new DocxTableCell({ children: [new Paragraph({ text: getCell(colSuggestion) })] }),
            new DocxTableCell({ children: attachmentChildren, width: { size: 20, type: WidthType.PERCENTAGE }, margins: { top: 100, bottom: 100, left: 100, right: 100 } }),
          ];
          tableRows.push(new DocxTableRow({ children: cells }));
        }
      }
    }

    const doc = new Document({
      sections: [{
        properties: { page: { size: { orientation: PageOrientation.LANDSCAPE } } },
        children: [new DocxTable({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: tableRows })],
      }],
    });

    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${checklist.name || 'checklist'}_fields.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating minimal Word document:', error);
  }
};
  const handleAssign = async () => {
    if (!assignEmail || !assigningChecklist) return;

    try {
      const newChecklist = {
        ...assigningChecklist,
        originalChecklistId: assigningChecklist.id,
        assignedTo: assignEmail,
        assignedAt: new Date().toISOString(),
        status: 'assigned',
      };
      newChecklist.tenantId = assigningChecklist.tenantId || tenantId || null;

      delete newChecklist.id; // Remove original ID to create a new document
      await addDoc(collection(db, "assignedChecklists"), newChecklist);

      setAssigningChecklist(null);
      setAssignEmail("");
      setIsAssignDialogOpen(false);

      // Show success message
      alert("Checklist assigned successfully!");

    } catch (error) {
      console.error("Error assigning checklist:", error);
      alert("Error assigning checklist. Please try again.");
    }
  };



  const handleAssignDialogOpen = (checklist) => {
    setAssigningChecklist(checklist);
    setIsAssignDialogOpen(true);
  };

  const handleAssignDialogClose = () => {
    setIsAssignDialogOpen(false);
    setAssigningChecklist(null);
    setAssignEmail("");
  };

  return (
    <>
      {showAlert && (
        <Box sx={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1500 }}>
          <Alert severity={alertSeverity} onClose={() => setShowAlert(false)}>{alertMessage}</Alert>
        </Box>
      )}



      {/* Admin Energy Reports Section */}
      <Box mt={4}>
        <Typography variant="h5" gutterBottom>
          Admin Energy Reports ({checklists.length})
        </Typography>
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checklists.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No energy reports available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  checklists.map((checklist) => (
                    <TableRow key={checklist.id} hover>
                      <TableCell>{checklist.name || "Untitled"}</TableCell>
                      <TableCell>
                        {checklist.description
                          ? checklist.description.length > 100
                            ? `${checklist.description.substring(0, 100)}...`
                            : checklist.description
                          : "No description"}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => onEditChecklist && onEditChecklist(checklist)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Assign">
                          <IconButton
                            color="secondary"
                            onClick={() => handleAssignDialogOpen(checklist)}
                          >
                            <Assignment />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteChecklist(checklist.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton
                            color="info"
                            onClick={() => generatePDF(checklist)}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Export to Word">
                          <IconButton
                            color="success"
                            onClick={() => handleExportWord(checklist)}
                          >
                            <Description />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Assignment Dialog */}
      <Dialog open={isAssignDialogOpen} onClose={handleAssignDialogClose}>
        <DialogTitle>Assign Checklist</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={assignEmail}
            onChange={(e) => setAssignEmail(e.target.value)}
            margin="normal"
            placeholder="Enter email address"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose}>Cancel</Button>
          <Button
            onClick={handleAssign}
            color="primary"
            disabled={!assignEmail.trim()}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>


      {/* CSS for loading spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default AdminChecklistView;